import React, { useState } from "react";
import { ILevel } from "src/types";
import showToast from "@utils/toast";
import { API_URL, userRole } from "src/service/config";
import fetchWithToken from "@utils/tokenUtils";

interface Props {
  level: ILevel | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const EditLevelModal = ({ level, onClose }: Props) => {
  const [formData, setFormData] = useState({
    title: level?.title || "",
    totalLessons: level?.totalLessons || 0,
    available: level?.available || false,
    description: level?.description || "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!level) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    const passedInputValues =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({
      ...prev,
      [name]: passedInputValues,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        title: formData.title,
        totalLessons: formData.totalLessons,
        available: formData.available,
        description: formData.description,
      };

      console.log(payload);

      const res = await fetchWithToken(`${API_URL}/levels/${level.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (res.ok) {
        showToast("Level updated successfully", true);
        onClose();
      } else {
        showToast(result.message || "Update failed", false);
      }
    } catch {
      showToast("Error updating level", false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-6 shadow-2xl relative border border-gray-200">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-lg"
        >
          ✕
        </button>

        <h2 className="text-base font-bold text-gray-800">
          Edit Level: <span className="text-brand">{level.title}</span>
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-600 font-medium block mb-1">
              Level Title
            </label>
            <input
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="e.g. Yoruba Essentials"
              className="input-style"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600 font-medium block mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              className="input-style"
              rows={4}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  description: e.target.value,
                });
              }}
              required
            ></textarea>
          </div>

          <div>
            <label className="text-sm text-gray-600 font-medium block mb-1">
              Total Lessons
            </label>
            <input
              name="totalLessons"
              type="number"
              value={formData.totalLessons}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  totalLessons: Number(e.target.value),
                });
              }}
              required
              placeholder="Enter number of lessons"
              className="input-style"
            />
          </div>

          {["admin", "editor"].includes(userRole) && (
            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                name="available"
                checked={formData.available}
                onChange={handleChange}
                className="scale-90 text-emerald-600"
              />
              <label className="text-sm text-gray-700">Available</label>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="button-primary w-full"
          >
            {isSubmitting ? "Updating..." : "Update Level"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditLevelModal;
